<template>
  <p-form-element class="p-autocomplete">
    <p-menu
      v-model="internalItemsVisible"
      :disabled="disabled || readonly"
      :open-on-click="openOnClick"
      min-width-of-reference
      bottom
      @input="setItemsVisible"
    >
      <template slot="activator">
        <p-text-field
          ref="text-field"
          :value="value"
          v-bind="$props"
          :messages="innerMessages"
          :errors="innerErrors"
          enterkeyhint="done"
          v-on="{
            ...$listeners,
            // TODO: Figure out why this is necessary
            // If this is not added the $listeners contains a listener to the local function onSearchInput
            input: (text) => $emit('input', text),
          }"
          @keydown.enter.prevent="onEnterDown"
          @keydown.escape.prevent="setItemsVisible(false)"
          @keydown.up.prevent.stop="selectPreviousOption"
          @keydown.down.prevent.stop="selectNextOption"
          @keydown.tab="onTabDown"
          @input="onSearch"
        ></p-text-field>
      </template>
      <p-surface class="dropdown">
        <slot name="prepend-list"></slot>
        <p-list-group>
          <p-list :dense="dense" :spacious="spacious">
            <slot name="prepend-list-entry"></slot>
            <p-list-entry
              v-for="(item, index) in items"
              :key="index"
              class="p-autocomplete-item"
              :class="{ active: selectedOptionIndex === index }"
              @click="onClickOption(item)"
            >
              <div
                :alt="item"
                class="list-item-content mx-2"
              >
                {{ getItemDisplay(item) }}
              </div>
            </p-list-entry>
            <slot name="append-list-entry"></slot>
          </p-list>
        </p-list-group>
        <slot name="append-list"></slot>
      </p-surface>
    </p-menu>
  </p-form-element>
</template>

<script lang="ts">
  import { PropType } from 'vue';
  import PTextField from '../p-text-field/p-text-field.vue';
  import interactable from '../../mixins/interactable/interactable';
  import colorable from '../../mixins/colorable/colorable';
  import mixins from '../../mixins';
  import sizeable from '../../mixins/sizeable/sizeable';
  import validatable from '../../mixins/validatable/validatable';
  import _ from '../../utils';

  export default mixins(interactable, colorable, sizeable, validatable).extend({
    mixins: [interactable, colorable, sizeable, validatable],
    props: {
      items: { default: () => [], type: Array as PropType<Record<string, any>[]> },
      itemsVisible: { default: false, type: Boolean },
      openOnClick: { default: true, type: Boolean },
      itemDisplay: { default: 'caption', type: [String, Number] },
      itemDetail: { default: 'detail', type: [String, Number] },
      itemValue: { default: 'id', type: [String, Number] },
      dense: { default: true, type: Boolean },
      ...((new PTextField()).$options.props),
      value: { default: undefined, type: [Number, String, Boolean, Object, Array] },
    },
    data: () => ({
      selectedOption: undefined as any | undefined,
      selectedOptionIndex: undefined as number | undefined,
      internalItemsVisible: false,
    }),
    watch: {
      items: {
        immediate: true,
        handler(newItems, oldItems) {
          if (_.isNil(newItems) || _.isNil(oldItems) || newItems.length !== oldItems.length) {
            this.setSelectedOption(undefined);
            return;
          }
          for (let i = 0; i < newItems.length; i += 1) {
            if (newItems?.[this.itemValue] !== oldItems?.[this.itemValue]
              || newItems?.[this.itemDisplay] !== oldItems?.[this.itemDisplay]) {
              this.setSelectedOption(undefined);
              return;
            }
          }
        },
      },
      itemsVisible: {
        immediate: true,
        handler(visible: boolean) {
          this.internalItemsVisible = this.itemsVisible;
        },
      },
    },
    methods: {
      /** @param item can be either a value or an item object */
      setSelectedOption(item: any) {
        const value = _.isObject(item) ? item[this.itemValue] : item;
        if (value === null || value === undefined) {
          this.selectedOption = undefined;
          this.selectedOptionIndex = undefined;
          return;
        }
        const foundIndex = this.items.findIndex((i) => i[this.itemValue] === value);
        if (foundIndex >= 0) {
          this.selectedOptionIndex = foundIndex;
          this.selectedOption = this.items[foundIndex];
        }
      },
      selectNextOption() {
        this.setItemsVisible(true);
        const nextIndex = Math.min((this.selectedOptionIndex ?? -1) + 1, this.items.length - 1);
        this.selectOption(this.items[nextIndex]);
      },
      selectPreviousOption() {
        this.setItemsVisible(true);
        const nextIndex = Math.max((this.selectedOptionIndex ?? 1) - 1, 0);
        this.selectOption(this.items[nextIndex]);
      },
      onEnterDown() {
        if (this.selectedOption) {
          this.selectOption(this.selectedOption);
        }
        this.setItemsVisible(false);
      },
      onTabDown() {
        this.$nextTick(() => {
          this.setItemsVisible(false);
        });
      },
      onClickOption(item: any) {
        this.selectOption(item);
        this.setItemsVisible(false);
      },
      setItemsVisible(visible: boolean) {
        this.internalItemsVisible = visible;
        this.$emit('update:itemsVisible', visible);
        this.$emit('update:items-visible', visible);
      },
      getItemDisplay(item: any): string {
        if (typeof item === 'object') {
          return item?.[this.itemDisplay];
        }
        return item;
      },
      getItemValue(item: any): string | number {
        if (typeof item === 'object') {
          return item?.[this.itemValue];
        }
        return item;
      },
      getItemDetail(item: any): string {
        if (typeof item === 'object') {
          return item?.[this.itemDetail];
        }
        return item;
      },
      onSearch(value: string) {
        this.$emit('search', value);
      },
      selectOption(item: any) {
        const itemDisplay = this.getItemDisplay(item);
        const itemValue = this.getItemValue(item);
        this.setSelectedOption(item);
        this.$emit('input', itemDisplay);
        this.$emit('select-item', item);
        this.$emit('select-value', itemValue);
      },
    },
  });
</script>
